import { packageText as pm, usePackageManagementLanguage } from "../../i18n/packageManagementPresentation";
/**
 * CancelJobConfirmDialog
 *
 * Alertdialog confirming that the user wants to stop a running job.
 * Subsystem-aware description copy.  Optional "Report this issue to
 * administrators" toggle surfaces a free-text field (≤ 500 chars).
 *
 * On confirm: calls onConfirm(body).  Caller owns the loading state
 * and error display — the dialog closes itself only on an explicit
 * cancel click or after onConfirm resolves without error.
 */

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useDialogFocus } from "../../hooks/useDialogFocus";
import { createPortal } from "react-dom";

import { Button } from "../ui/Button.js";
import { Icon } from "../ui/Icon.js";
import { cn } from "../../utils/cn.js";
import type { JobScope, CancelJobBody } from "./cancelJob.js";

// ── Copy ──────────────────────────────────────────────────────────────────────

const SCOPE_COPY = {
  "package-user": "cancelPackageWarning", "package-admin": "cancelPackageWarning",
  vpn: "cancelVpnWarning", ssl: "cancelSslWarning", update: "cancelUpdateWarning",
} as const;

const MAX_MESSAGE = 500;

// ── Types ─────────────────────────────────────────────────────────────────────

type CancelJobConfirmDialogProps = {
  open: boolean;
  scope: JobScope;
  /** Truthy while the abort request is in-flight. */
  loading?: boolean;
  /** Error message to show inline when the abort request failed. */
  error?: string | null;
  onClose: () => void;
  onConfirm: (body: CancelJobBody) => void;
};

// ── Component ─────────────────────────────────────────────────────────────────

export const CancelJobConfirmDialog = ({
  open,
  scope,
  loading = false,
  error = null,
  onClose,
  onConfirm,
}: CancelJobConfirmDialogProps) => {
  usePackageManagementLanguage();
  const [report, setReport] = useState(false);
  const [message, setMessage] = useState("");
  const titleId = useId();
  const descId = useId();
  const keepRunningRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setReport(false);
      setMessage("");
    }
  }, [open]);

  // Lands on "Keep running", never the destructive confirm; Escape waits out an in-flight abort.
  useDialogFocus(containerRef, { onClose, initialFocusRef: keepRunningRef, canClose: !loading, enabled: open });

  const handleConfirm = useCallback(() => {
    const body: CancelJobBody = {
      report,
      ...(report && message.trim() ? { message: message.trim() } : {}),
    };
    onConfirm(body);
  }, [report, message, onConfirm]);

  if (!open) return null;

  const charsLeft = MAX_MESSAGE - message.length;
  const overLimit = charsLeft < 0;

  const dialog = (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-in fade-in-0 duration-150"
        onClick={loading ? undefined : onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        ref={containerRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        className="relative w-full max-w-md mx-4 animate-in fade-in-0 zoom-in-95 duration-150"
      >
        <div className="rounded-xl border border-white/[0.08] bg-card shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="flex items-start gap-3 px-5 pt-5 pb-4">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-red-500/10 border border-red-500/20">
              <Icon icon="lucide:octagon-x" className="h-4 w-4 text-red-400" />
            </div>
            <div className="flex-1 min-w-0">
              <h2
                id={titleId}
                className="text-sm font-semibold text-foreground"
              >
                {pm("cancelQuestion")}</h2>
              <p id={descId} className="mt-1 text-xs text-muted-foreground leading-relaxed">
                {pm(SCOPE_COPY[scope])}
              </p>
            </div>
          </div>

          {/* Body */}
          <div className="px-5 pb-2 space-y-3">
            {/* Report toggle */}
            <label className="flex items-center gap-3 cursor-pointer group">
              <div className="relative flex-shrink-0">
                <input
                  type="checkbox"
                  checked={report}
                  onChange={(e) => setReport(e.target.checked)}
                  className="sr-only peer"
                />
                <div
                  className={cn(
                    "h-4 w-4 rounded border transition-colors",
                    "border-white/20 bg-white/[0.04]",
                    "peer-checked:bg-primary peer-checked:border-primary",
                    "peer-focus-visible:ring-2 peer-focus-visible:ring-primary/70 peer-focus-visible:ring-offset-1 peer-focus-visible:ring-offset-background",
                  )}
                  aria-hidden="true"
                >
                  {report && (
                    <Icon
                      icon="lucide:check"
                      className="h-3 w-3 text-primary-foreground absolute top-0.5 left-0.5"
                    />
                  )}
                </div>
              </div>
              <span className="text-xs text-muted-foreground group-hover:text-foreground transition-colors">
                {pm("reportAdmins")}</span>
            </label>

            {/* Message field — shown when report toggle is on */}
            {report && (
              <div className="space-y-1.5 animate-in fade-in-0 slide-in-from-top-1 duration-150">
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE + 10))}
                  placeholder={pm("adminMessagePlaceholder")}
                  rows={3}
                  className={cn(
                    "w-full resize-none rounded-md border bg-white/[0.03] px-3 py-2",
                    "text-xs text-foreground placeholder:text-muted-foreground",
                    "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70",
                    overLimit
                      ? "border-red-500/60 focus-visible:ring-red-500/70"
                      : "border-white/[0.08]",
                  )}
                  aria-label={pm("adminMessage")}
                  aria-describedby="cancel-msg-counter"
                />
                <div
                  id="cancel-msg-counter"
                  aria-live="polite"
                  className={cn(
                    "text-right text-[10px] tabular-nums",
                    overLimit ? "text-red-400" : "text-muted-foreground/50",
                  )}
                >
                  {charsLeft < 100 ? pm("charactersLeft", { count: charsLeft }) : `${message.length} / ${MAX_MESSAGE}`}
                </div>
              </div>
            )}

            {/* Inline error from failed abort attempt */}
            {error && (
              <div className="flex items-start gap-2 rounded-md border border-red-500/30 bg-red-500/5 px-3 py-2">
                <Icon icon="lucide:alert-triangle" className="h-3.5 w-3.5 text-red-400 mt-0.5 flex-shrink-0" />
                <span className="text-xs text-red-400">{error}</span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-white/[0.06]">
            <Button
              ref={keepRunningRef}
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClose}
              disabled={loading}
            >
              {pm("keepRunning")}</Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={handleConfirm}
              disabled={loading || overLimit}
            >
              {loading ? (
                <>
                  <Icon icon="lucide:loader-2" className="h-3.5 w-3.5 animate-spin" />
                  {pm("stopping")}</>
              ) : (
                <>
                  <Icon icon="lucide:square" className="h-3.5 w-3.5" />
                  {pm("cancelJob")}</>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(dialog, document.body);
};
